import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { customerAPI, auth } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, ShoppingCart, Copy, Check } from 'lucide-react';

export default function PurchaseHistory() {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    // Check if user is authenticated
    if (!auth.isAuthenticated()) {
      navigate('/login');
      return;
    }

    loadPurchases();
  }, [navigate]);

  const loadPurchases = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await customerAPI.getPurchases();
      setPurchases(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load purchase history');
    } finally {
      setLoading(false);
    }
  };

  const copyLicenseKey = (licenseKey, purchaseId) => {
    navigator.clipboard.writeText(licenseKey);
    setCopiedKey(purchaseId);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: 'default',
      pending: 'secondary',
      failed: 'destructive',
      refunded: 'outline',
    };

    return (
      <Badge variant={variants[status] || 'secondary'}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 pt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-12 w-64 mb-6 bg-gray-800" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48 bg-gray-800" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 pt-20">
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Purchase History</h1>
          <p className="text-sm text-gray-400">View all your past purchases</p>
        </div>
        {error && (
          <Alert variant="destructive" className="mb-6 bg-red-900/50 border-red-500/50 text-red-200">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {purchases.length === 0 ? (
          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardContent className="py-12">
              <div className="text-center">
                <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-white">No purchases</h3>
                <p className="mt-1 text-sm text-gray-400">
                  You haven't made any purchases yet.
                </p>
                <div className="mt-6">
                  <Button asChild className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                    <Link to="/pricing">Buy License</Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {purchases.map((purchase) => (
              <Card key={purchase.id} className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg text-white">
                        Cogsmith Pro License
                      </CardTitle>
                      <CardDescription className="text-gray-400">
                        {formatDate(purchase.created_at)}
                      </CardDescription>
                    </div>
                    {getStatusBadge(purchase.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Purchase Details */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <label className="font-medium text-purple-400">Amount</label>
                        <p className="text-gray-200">
                          ${purchase.amount.toFixed(2)} {purchase.currency.toUpperCase()}
                        </p>
                      </div>

                      <div>
                        <label className="font-medium text-purple-400">Payment Method</label>
                        <p className="text-gray-200 capitalize">
                          {purchase.payment_platform}
                        </p>
                      </div>

                      <div>
                        <label className="font-medium text-purple-400">Payment ID</label>
                        <p className="text-gray-200 font-mono text-xs">
                          {purchase.payment_id}
                        </p>
                      </div>

                      <div>
                        <label className="font-medium text-purple-400">Status</label>
                        <p className="text-gray-200 capitalize">
                          {purchase.status}
                        </p>
                      </div>
                    </div>

                    {/* License Key */}
                    {purchase.license_key && (
                      <div>
                        <label className="text-sm font-medium text-purple-400">License Key</label>
                        <div className="mt-1 font-mono text-sm bg-gray-800/50 p-3 rounded flex items-center justify-between text-gray-200">
                          <span className="break-all">{purchase.license_key}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyLicenseKey(purchase.license_key, purchase.id)}
                            className="ml-2 flex-shrink-0 hover:bg-gray-700"
                          >
                            {copiedKey === purchase.id ? (
                              <Check className="h-4 w-4 text-green-400" />
                            ) : (
                              <Copy className="h-4 w-4 text-gray-400" />
                            )}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    {purchase.status === 'completed' && (
                      <div className="flex gap-2 pt-2">
                        <Button variant="outline" size="sm" disabled className="bg-gray-800/50 border-purple-500/30 text-gray-400">
                          Download Invoice
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

